import { useEffect, useRef } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';

/**
 * GlobalTranslator — whole-platform UI text auto-translation.
 *
 * When a non-English language is active, this walks visible text nodes under
 * <body>, batch-translates them (cached server-side + client-side) and applies
 * the result, then keeps them in sync via a debounced MutationObserver. This
 * delivers ~100% interface coverage (admin panel, modals, buttons, forms,
 * toasts…) without wrapping every string by hand. The component-level hooks
 * (TranslatableText / useTranslatedMap) still provide instant reactive
 * translation for key content; this layer catches the long tail.
 *
 * Safety:
 *  - English is a complete no-op (and restores originals on switch-back).
 *  - Skips scripts/styles/inputs/code, [data-no-translate] subtrees, and
 *    nodes with no letters (numbers, prices, symbols, emoji).
 *  - Stores each node's English original so it can restore / re-translate when
 *    the language changes.
 */
const SKIP_TAGS = new Set([
  'SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA', 'INPUT', 'SELECT', 'OPTION',
  'CODE', 'PRE', 'SVG', 'PATH', 'CANVAS', 'IFRAME', 'VIDEO', 'AUDIO',
]);

const hasLetter = (s) => /[A-Za-z]/.test(s);

export default function GlobalTranslator() {
  const { language, translateContent, setTranslating } = useLanguage();
  const store = useRef(new WeakMap()); // node -> { orig, translated }
  const tracked = useRef(new Set());   // nodes touched (for restore)

  useEffect(() => {
    // 1) Restore previous language's nodes back to the English baseline
    tracked.current.forEach((node) => {
      const rec = store.current.get(node);
      if (rec) {
        try { if (node.nodeValue !== rec.orig) node.nodeValue = rec.orig; } catch { /* detached */ }
      }
    });
    tracked.current = new Set();
    store.current = new WeakMap();

    if (language === 'en') return;

    let observer;
    let timer;
    let running = false;

    const skip = (textNode) => {
      let el = textNode.parentElement;
      while (el) {
        if (SKIP_TAGS.has(el.tagName)) return true;
        if (el.nodeType === 1 && (el.hasAttribute('data-no-translate') ||
            el.getAttribute('contenteditable') === 'true')) return true;
        el = el.parentElement;
      }
      return false;
    };

    const collect = () => {
      const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
        acceptNode(n) {
          const v = n.nodeValue;
          if (!v) return NodeFilter.FILTER_REJECT;
          const t = v.trim();
          if (t.length < 2 || !hasLetter(t)) return NodeFilter.FILTER_REJECT;
          const rec = store.current.get(n);
          if (rec && n.nodeValue === rec.translated) return NodeFilter.FILTER_REJECT; // already done
          if (skip(n)) return NodeFilter.FILTER_REJECT;
          return NodeFilter.FILTER_ACCEPT;
        },
      });
      const nodes = [];
      const texts = new Set();
      let cur;
      while ((cur = walker.nextNode())) {
        nodes.push(cur);
        texts.add(cur.nodeValue.trim());
      }
      return { nodes, texts: [...texts] };
    };

    const run = async () => {
      if (running) return;
      running = true;
      try {
        const { nodes, texts } = collect();
        if (!texts.length) return;
        const map = await translateContent(texts);
        nodes.forEach((n) => {
          const orig = n.nodeValue;
          const key = orig.trim();
          const tr = map[key];
          if (tr && tr !== key) {
            const lead = orig.match(/^\s*/)[0];
            const trail = orig.match(/\s*$/)[0];
            const newVal = lead + tr + trail;
            store.current.set(n, { orig, translated: newVal });
            tracked.current.add(n);
            if (n.nodeValue !== newVal) {
              try { n.nodeValue = newVal; } catch { /* detached */ }
            }
          }
        });
      } finally {
        running = false;
      }
    };

    const schedule = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(run, 150);
    };

    // initial pass (give the route a moment to paint)
    setTranslating(true);
    const startTimer = setTimeout(async () => {
      await run();
      setTranslating(false);
    }, 80);
    observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });

    // Trigger an immediate pass on client-side route changes so freshly-mounted
    // routes don't briefly flash English before the observer's debounce fires.
    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;
    const onNav = () => { setTimeout(run, 30); };
    window.history.pushState = function (...args) { const r = origPush.apply(this, args); onNav(); return r; };
    window.history.replaceState = function (...args) { const r = origReplace.apply(this, args); onNav(); return r; };
    window.addEventListener('popstate', onNav);

    return () => {
      clearTimeout(startTimer);
      if (timer) clearTimeout(timer);
      if (observer) observer.disconnect();
      window.history.pushState = origPush;
      window.history.replaceState = origReplace;
      window.removeEventListener('popstate', onNav);
      setTranslating(false);
    };
  }, [language, translateContent, setTranslating]);

  return null;
}
